import { useEffect, useState } from 'react'
import { Form, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Group, Stack } from '@mantine/core'
import * as yup from 'yup'

import { EmailLink } from '@/components/common/EmailLink'
import { NotesForm } from '@/components/Event/NewScript/components/common/forms/NotesForm'
import { SpellingsForm } from '@/components/Event/NewScript/components/common/forms/SpellingsForm'
import { TimestampsForm } from '@/components/Event/NewScript/components/common/forms/TimestampsForm'
import { ActionButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionButton'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionTitle'
import { ContinueButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ContinueButton'
import { ScriptTimeline } from '@/components/Event/NewScript/components/common/ScriptTimeline/ScriptTimeline'
import { TimelineAction } from '@/components/Event/NewScript/components/common/ScriptTimeline/TimelineAction'
import { ExhibitsSection } from '@/components/Event/NewScript/components/Exhibits/ExhibitsSection'
import { QuestioningVerbatim } from '@/components/Event/NewScript/components/Questioning/QuestioningVerbatim'
import {
  DR_NOTES_CHAR_LIMIT,
  DR_SPELLINGS_CHAR_LIMIT,
  DR_TIMESTAMPS_CHAR_LIMIT,
} from '@/components/Event/NewScript/constants'
import { useScriptFlowState } from '@/components/Event/NewScript/hooks/state/useScriptFlowState'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import { getMaxCharactersMessage } from '@/components/Event/NewScript/schemas/validation'
import { getWitnessEventTitle } from '@/components/Event/NewScript/utils/event'
import { formatStepKey } from '@/components/Event/NewScript/utils/stepKey'
import { DBFContact, ScriptStep, TimestampType } from '@/constants'
import { FeatureFlags } from '@/featureFlags'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { ScriptStepProps } from '@/types'
import {
  formatTimeInTimezoneWithAbbreviation,
  getEventTimezone,
} from '@/utils/time'

const ConductingStepSchema = yup.object().shape({
  timestamps: yup
    .string()
    .max(
      DR_TIMESTAMPS_CHAR_LIMIT,
      getMaxCharactersMessage(DR_TIMESTAMPS_CHAR_LIMIT),
    )
    .nullable()
    .default(null),
  spellings: yup
    .string()
    .max(
      DR_SPELLINGS_CHAR_LIMIT,
      getMaxCharactersMessage(DR_SPELLINGS_CHAR_LIMIT),
    )
    .nullable()
    .default(null),
  notes: yup
    .string()
    .max(DR_NOTES_CHAR_LIMIT, getMaxCharactersMessage(DR_NOTES_CHAR_LIMIT))
    .nullable()
    .default(null),
})

export const QUESTIONING_ON_RECORD_CLOSING_REMINDER =
  'After questioning is complete, stay ON the record as you move onto the closing stage.'
export const QUESTIONING_OFF_RECORD_CONTINUE_REMINDER =
  'You must be on the record to move forward.'

export const QuestioningStep = ({ event }: ScriptStepProps) => {
  const { actions, state, currentEvent, currentEventIndex, witness } =
    useScriptFlowState()
  const currentWitnessEvent = currentEvent!
  const [cancellationFlowStarted, setCancellationFlowStarted] = useState(false)
  const drExhibitsEnabled = useFeatureFlag(FeatureFlags.DR_EXHIBITS)
  const {
    completeAction,
    canContinue,
    isActionCompleted,
    completeStepAndContinue,
  } = useStepActions(state.current_step, event)

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(ConductingStepSchema),
    defaultValues: {
      timestamps: currentWitnessEvent.timestamps,
      spellings: currentWitnessEvent.spellings,
      notes: currentWitnessEvent.notes,
    },
  })

  useEffect(() => {
    const subscription = methods.watch((value, { type }) => {
      // We store here only values that are changed by typing
      if (type !== 'change') {
        return
      }

      actions.updateConductingData({
        notes: value.notes,
        timestamps: value.timestamps,
        spellings: value.spellings,
      })
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, state, actions])

  const addNewTimestamp = (timestampType: TimestampType) => {
    const res = actions.addTimestamp({
      isOnRecord:
        timestampType === TimestampType.EXHIBIT
          ? undefined
          : timestampType === TimestampType.RECORDING_ON,
      timestampType: timestampType,
      timezone: getEventTimezone(event),
    })
    methods.setValue(
      'timestamps',
      res.events[currentEventIndex ?? 0].timestamps,
    )
  }

  const validateAndCompleteAction = (key: string) => {
    methods.trigger().then(isValid => {
      if (isValid) {
        completeAction(key)
      }
    })
  }

  const shouldShowClosingRecordReminder =
    currentWitnessEvent.is_on_record && !cancellationFlowStarted

  const canContinueToClosing =
    canContinue() &&
    (currentWitnessEvent.is_on_record || cancellationFlowStarted)

  const continueReminder =
    !currentWitnessEvent.is_on_record && !cancellationFlowStarted
      ? QUESTIONING_OFF_RECORD_CONTINUE_REMINDER
      : undefined

  return (
    <FormProvider {...methods}>
      <Form>
        <Stack align="flex-start" spacing="xl">
          <ScriptTimeline
            title={
              state.events.length > 1
                ? `${(currentEventIndex ?? 0) + 2}.2 Questioning - ${getWitnessEventTitle(event, witness?.name)}`
                : '4. Questioning'
            }
          >
            <TimelineAction
              actionKey="start_questioning"
              title="Let counsels know they can start questioning"
              onComplete={completeAction}
              completed={isActionCompleted('start_questioning')}
            >
              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    Okay, whenever you are ready, counsel, you may proceed with
                    questioning.
                  </ActionText>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      Mute your microphone and stay on camera while on the
                      record. <br />
                      Remain professional and attentive throughout the entire
                      proceeding.
                      <br />
                      Monitor audio integrity and request repetition or
                      clarification, as necessary.
                    </ActionText>
                  </ActionContainer>
                </Stack>
              </ActionContainer>
            </TimelineAction>
            <TimelineAction
              actionKey="oversight_the_progress"
              title="Monitor the event"
              onComplete={validateAndCompleteAction}
              completed={isActionCompleted('oversight_the_progress')}
            >
              <Stack>
                {/* Go off record etc. */}
                <ActionContainer outer>
                  <Stack>
                    <ActionContainer>
                      <Stack>
                        <Group>
                          <ActionButton
                            onClick={() =>
                              addNewTimestamp(TimestampType.RECORDING_OFF)
                            }
                            disabled={!currentWitnessEvent.is_on_record}
                          >
                            Go off record
                          </ActionButton>
                          {!drExhibitsEnabled && (
                            <ActionButton
                              onClick={() =>
                                addNewTimestamp(TimestampType.EXHIBIT)
                              }
                              disabled={!currentWitnessEvent.is_on_record}
                            >
                              Exhibit shown
                            </ActionButton>
                          )}
                        </Group>
                        {shouldShowClosingRecordReminder && (
                          <ActionText>
                            {QUESTIONING_ON_RECORD_CLOSING_REMINDER}
                          </ActionText>
                        )}
                        {!currentWitnessEvent.is_on_record && (
                          <Stack>
                            <Stack py="md">
                              <ActionText mono>
                                We are now OFF the record and the time is{' '}
                                {currentWitnessEvent.last_timestamp &&
                                  formatTimeInTimezoneWithAbbreviation(
                                    currentWitnessEvent.last_timestamp,
                                    getEventTimezone(event),
                                  )}
                              </ActionText>
                              <ActionText mono>{`<After break>`}</ActionText>
                              <ActionText mono>
                                <QuestioningVerbatim event={event} />
                              </ActionText>
                            </Stack>
                            <ActionButton
                              onClick={() =>
                                addNewTimestamp(TimestampType.RECORDING_ON)
                              }
                            >
                              Go on record
                            </ActionButton>
                          </Stack>
                        )}
                      </Stack>
                    </ActionContainer>
                    {drExhibitsEnabled && (
                      <ActionContainer>
                        <ExhibitsSection event={event} />
                      </ActionContainer>
                    )}
                    <ActionContainer>
                      <ActionTitle>Reminder</ActionTitle>
                      <ActionText>
                        Breaks must be unanimous. If there is an objection from
                        either side, objection must be resolved before reading
                        off the record. Note the exhibit and exhibit # for the
                        record. If the witness or attorney shows any document,
                        make sure to clarify if it’s being used as an exhibit or
                        just as a reference.
                      </ActionText>
                    </ActionContainer>
                    <ActionContainer>
                      <ActionTitle>Reminder</ActionTitle>
                      <ActionText>
                        If counsel have exhibits to share, please email the
                        exhibit files to{' '}
                        <EmailLink
                          size="sm"
                          email={DBFContact.EXHIBITS_EMAIL}
                        />{' '}
                        after the event so they can be attached to the
                        transcript.
                      </ActionText>
                    </ActionContainer>
                    <ActionContainer>
                      <TimestampsForm />
                    </ActionContainer>
                  </Stack>
                </ActionContainer>

                <ActionContainer outer>
                  <Stack>
                    <ActionContainer>
                      <SpellingsForm />
                    </ActionContainer>
                    <ActionContainer>
                      <ActionTitle>Reminder</ActionTitle>
                      <ActionText>
                        If the witness or attorney uses a name, a location, or a
                        term that are not common words, take note of it and at
                        the end of all the questioning or at a natural break,
                        circle back and ask for the proper spelling.
                      </ActionText>
                    </ActionContainer>
                  </Stack>
                </ActionContainer>

                <ActionContainer outer>
                  <ActionContainer>
                    <NotesForm />
                  </ActionContainer>
                </ActionContainer>
                {shouldShowClosingRecordReminder && (
                  <ActionContainer outer>
                    <ActionText>
                      {QUESTIONING_ON_RECORD_CLOSING_REMINDER}
                    </ActionText>
                  </ActionContainer>
                )}
              </Stack>
            </TimelineAction>
            <ContinueButton
              event={event}
              label="Continue to Closing"
              canContinue={canContinueToClosing}
              onCancelSwitchClick={setCancellationFlowStarted}
              reminder={continueReminder}
              step={ScriptStep.QUESTIONING}
              handleClick={() =>
                completeStepAndContinue(
                  formatStepKey(ScriptStep.CLOSING, currentEventIndex),
                )
              }
            />
          </ScriptTimeline>
        </Stack>
      </Form>
    </FormProvider>
  )
}
